import React from "react";

interface Column<T> {
  label: string | React.ReactNode;
  render?: (item: T, index: number, isBranch: boolean) => React.ReactNode;
}

interface ListViewProps<T> {
  data: T[];
  columns: Column<T>[];
  onRowClick?: (item: T) => void;
  getBranches?: (item: T) => T[];
}

function ListView<T>({
  data,
  columns,
  onRowClick,
  getBranches,
}: ListViewProps<T>) {
  return (
    <div className="bg-[#F7F6F2] pt-2 rounded-r-3xl rounded-b-3xl overflow-hidden">
      <div className="overflow-x-auto bg-white rounded-r-3xl rounded-b-3xl">
        <table className="w-full text-sm border-collapse whitespace-nowrap">
          <thead className="text-[#131327]/80 border-b border-dashed border-gray-300">
            <tr>
              {columns.map((col, i) => (
                <th
                  key={i}
                  className="font-medium px-5 py-3 text-left border-r border-dashed border-gray-200 last:border-r-0 whitespace-nowrap"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.map((parent: any, pIdx) => {
              const branches = getBranches ? getBranches(parent) : [];
              const key = parent.id || pIdx;

              return (
                <React.Fragment key={`p-${key}`}>
                  {/* Parent row */}
                  <tr
                    className="bg-white hover:bg-[#FBD037]/20 border-b border-gray-100 cursor-pointer"
                    onClick={() => onRowClick?.(parent)}
                  >
                    {columns?.map((col, cIdx) => (
                      <td
                        key={cIdx}
                        className={`px-5 py-3.5 border-r border-dashed border-gray-100 last:border-r-0 ${
                          cIdx === 0 ? "w-12" : ""
                        }`}
                      >
                        {col.render?.(
                          {
                            ...parent,
                            _isBranch: false,
                            _parentIndex: pIdx + 1,
                          },
                          pIdx,
                          false,
                        )}
                      </td>
                    ))}
                  </tr>

                  {/* Branch rows */}
                  {branches?.length > 0 &&
                    branches.map((branch: any, bIdx) => {
                      const isLastBranch = bIdx === branches.length - 1;

                      return (
                        <tr
                          key={`b-${key}-${branch.id || bIdx}`}
                          className="bg-[#FBD037]/10 hover:bg-[#FBD037]/20 border-b border-gray-50"
                        >
                          {columns.map((col, cIdx) => (
                            <td
                              key={cIdx}
                              className={`py-3 border-r border-dashed border-gray-100 last:border-r-0 relative px-5`}
                            >
                              {/* Tree connector - only in the S/N or Name column (usually column 1 or 2) */}
                              {cIdx === 0 && (
                                <div className="absolute inset-0 pointer-events-none">
                                  <div className="relative h-full w-8">
                                    <div
                                      className={`absolute left-4 w-px bg-gray-300 ${
                                        isLastBranch
                                          ? "top-0 h-1/2"
                                          : "top-0 bottom-0"
                                      }`}
                                    />

                                    <div className="absolute left-4 top-1/2 w-5 h-px bg-gray-300" />
                                  </div>
                                </div>
                              )}

                              <div
                                className={`relative z-10 flex ${col?.label === "Status" || col?.label === "Actions" ? "justify-start" : "items-start"}`}
                              >
                                {col.render?.(
                                  {
                                    ...branch,
                                    _isBranch: true,
                                    parentName: parent.name,
                                    _parentIndex: pIdx + 1,
                                  },
                                  bIdx,
                                  true,
                                )}
                              </div>
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ListView;
